import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useState } from 'react'
import {
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  CircularProgress,
  LinearProgress,
  Alert,
  SelectChangeEvent,
  FormControlLabel,
  Checkbox,
  IconButton,
} from '@mui/material'
import VideocamIcon from '@mui/icons-material/Videocam'
import VideocamOffIcon from '@mui/icons-material/VideocamOff'
import MicIcon from '@mui/icons-material/Mic'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import MicOffIcon from '@mui/icons-material/MicOff'
import SettingsIcon from '@mui/icons-material/Settings'
import CameraAltIcon from '@mui/icons-material/CameraAlt'
import WallpaperIcon from '@mui/icons-material/Wallpaper'
import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { useDispatch } from 'react-redux'
import { setLoadingGame } from '../stores/GameStore'
import Network from '../services/Network'
import { zIndexLayers } from '../MuiTheme'
import { useAppSelector } from '../hooks'
import { listedWithoutIds, pickListedDevice, useMediaDevices } from '../hooks/useMediaDevices'
import { useMicLevel } from '../hooks/useMicLevel'
import { usePreviewHandoverUnderlay } from '../hooks/usePreviewHandoverUnderlay'
import { playSpeakerTest } from '../utils/speakerTest'
import phaserGame from '../PhaserGame'
import { emitDialogOpened, emitDialogClosed } from '../utils/pageVisibilityHandler'
import { requestNotificationPermission } from '../utils/notifications'
import { writeMediaPreference } from '../utils/mediaPreferences'
import { trackEvent, stripUndefinedAnalyticsProps } from '../services/analyticsService'
import { alpha, useTheme } from '@mui/material/styles'
import { shapeBorderRadiusPx } from '../theme/shapeSx'
import { describeMediaDevice } from '../utils/mediaDeviceUtils'

import BackgroundEffectsDialog from './BackgroundEffectsDialog'
import BackgroundProblemNotice from './BackgroundProblemNotice'
import {
  NO_BACKGROUND_EFFECT,
  backgroundProblemHoldsCamera,
  describeBackgroundEffect,
  isBackgroundEffectAvailable,
  preloadBackgroundPresetImages,
} from '../services/liveKitVideoBackground'
import {
  backgroundProblemTooltip,
  type BackgroundProblemAction,
} from '../services/backgroundProblemCopy'

// Import styled components
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  VideoPreviewContainer,
  NoVideoFeedback,
  MediaControlsWrapper,
  MediaControlButton,
  DeviceSelectCard,
  StyledVideo,
  previewPreparingStyle,
  previewSettledStyle,
  previewCoverSx,
  previewProgressSx,
  previewUnderlayStyle,
  PREVIEW_EFFECT_TRANSITION,
  LoadingWrapper,
  PermissionStatusContainer,
  StatusIcon,
} from './styles/PermissionsDialog.styles'

/**
 * Permissions dialog for camera and microphone access
 * This implementation uses native MediaStream
 */

export interface InviteLinkGateState {
  loading: boolean
  blocked: boolean
  blockMessage?: string
}

interface PermissionsDialogProps {
  /** Whether the dialog is open */
  open: boolean
  /** Callback function when the dialog is closed */
  onClose: () => void
  /** Callback when permissions are granted; `joinNearHost` reflects the invite checkbox at click time (if shown). */
  onPermissionsGranted: (detail?: { joinNearHost?: boolean }) => void | Promise<void>
  /** Network instance for handling connections */
  network?: Network
  /** Server-backed invite: preview loading / company block */
  inviteGate?: InviteLinkGateState
  /** Optional “spawn near host” toggle for invite links */
  inviteJoinNear?: {
    visible: boolean
    checked: boolean
    onChange: (value: boolean) => void
  }
}

/**
 * The longest Join waits for a picked background: the image fetch (8 s), the
 * preview's first frame (10 s) and a margin. Past it Join is enabled again and
 * the call applies the pick itself, as it does for any saved one.
 */
export const JOIN_BACKGROUND_WAIT_CAP_MS = 20000

export const PermissionsDialog: React.FC<PermissionsDialogProps> = ({
  open,
  onClose,
  onPermissionsGranted,
  network,
  inviteGate,
  inviteJoinNear,
}): JSX.Element => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const settings = useAppSelector((state) => state.workspace.settings)
  const platformName = settings?.platform_name || 'Sevv Industries'

  const alertWarningSx = useMemo(
    () => ({
      backgroundColor: alpha(theme.palette.warning.main, 0.1),
      border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
      color: theme.palette.warning.dark,
    }),
    [theme]
  )

  const alertErrorSx = useMemo(
    () => ({
      backgroundColor: alpha(theme.palette.error.main, 0.1),
      border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
      color: theme.palette.error.dark,
    }),
    [theme]
  )

  const deviceSelectSx = useMemo(
    () => ({
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.divider,
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.35),
        },
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      },
      '& .MuiSelect-select': {
        color: theme.palette.text.primary,
      },
    }),
    [theme]
  )

  const deviceInputLabelSx = useMemo(
    () => ({
      color: theme.palette.text.secondary,
      '&.Mui-focused': {
        color: theme.palette.primary.main,
      },
    }),
    [theme]
  )

  const joinButtonSx = useMemo(
    () => ({
      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
      color: theme.palette.primary.contrastText,
      border: 'none',
      borderRadius: shapeBorderRadiusPx(theme),
      fontWeight: 500,
      textTransform: 'none' as const,
      minHeight: 36,
      boxShadow: `0 4px 14px 0 ${alpha(theme.palette.primary.main, 0.3)}`,
      '&:hover': {
        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
        boxShadow: `0 10px 25px 0 ${alpha(theme.palette.primary.main, 0.4)}`,
      },
      '&:disabled': {
        background: theme.palette.action.disabledBackground,
        color: theme.palette.action.disabled,
        boxShadow: 'none',
      },
    }),
    [theme]
  )

  const cancelButtonSx = useMemo(
    () => ({
      color: theme.palette.text.secondary,
      border: `1px solid ${theme.palette.divider}`,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        borderColor: theme.palette.divider,
      },
    }),
    [theme]
  )

  const {
    state,
    setState,
    videoRef,
    handleDeviceSelection,
    handleVideoToggle,
    handleAudioToggle,
    handleRefresh,
    stopPreviewTracks,
    backgroundEffect,
    handleBackgroundEffectChange,
    previewShowing,
    previewStream,
    previewHandoverFrom,
    previewUnderlayStream,
    previewEffectPending,
    previewEffectWorking,
    previewPainted,
    previewProblem,
    backgroundEffectApplying,
    retryBackgroundEffect,
    enableCameraWithoutBackground,
  } = useMediaDevices(open)
  // SC-438: the level bar shows the chosen microphone is heard; the test chime checks the output.
  const micLevel = useMicLevel(state.stream, open && state.audioEnabled)
  const [speakerTestPlaying, setSpeakerTestPlaying] = useState(false)
  const handleSpeakerTest = useCallback(async () => {
    setSpeakerTestPlaying(true)
    try {
      await playSpeakerTest()
    } finally {
      setSpeakerTestPlaying(false)
    }
  }, [])

  const [backgroundDialogOpen, setBackgroundDialogOpen] = useState(false)

  /** Held as state so the underlay is bound once its element has mounted. */
  const [underlayElement, setUnderlayElement] = useState<HTMLVideoElement | null>(null)
  /** The raw view a pick was made over, showing while the preview has not painted. */
  const previewUnderlayShowing = usePreviewHandoverUnderlay(
    underlayElement,
    previewUnderlayStream,
    previewStream
  )

  /**
   * A problem that keeps the camera down is said on the preview itself, which
   * is covered for it whenever there is a camera to cover. The notice beside
   * the preview then carries only what can be done, tied to those words.
   */
  const previewProblemMessageId = useId()
  const previewHasVideo = state.videoEnabled && (state.stream?.getVideoTracks().length ?? 0) > 0
  const previewShowsProblem =
    previewHasVideo && previewEffectPending && backgroundProblemHoldsCamera(previewProblem)

  /** The Join screen's answer to each action a background problem offers. */
  const handleBackgroundProblemAction = useCallback(
    (action: BackgroundProblemAction) => {
      switch (action) {
        case 'retry':
          retryBackgroundEffect()
          break
        case 'pick-another':
          setBackgroundDialogOpen(true)
          break
        case 'use-blur':
          handleBackgroundEffectChange({ kind: 'blur', intensity: 'light' })
          break
        case 'turn-off':
          handleBackgroundEffectChange(NO_BACKGROUND_EFFECT)
          break
        case 'camera-without-background':
          enableCameraWithoutBackground()
          break
      }
    },
    [retryBackgroundEffect, handleBackgroundEffectChange, enableCameraWithoutBackground]
  )
  // Evaluated once: neither the build flag nor device capability changes within
  // a session, and the probe touches WebGL.
  const backgroundEffectsAvailable = useMemo(() => isBackgroundEffectAvailable(), [])

  // Warm the bundled plates as the Join screen opens, not only once the choices
  // are opened: a pick from "No effect" keeps the camera on screen until the
  // plate is in, and a cached plate makes that wait a frame or two.
  useEffect(() => {
    if (open && backgroundEffectsAvailable) preloadBackgroundPresetImages()
  }, [open, backgroundEffectsAvailable])

  const hasBackgroundEffect = backgroundEffect.kind !== 'none'
  // Shorter than "Choose background & effects", which wrapped in this column
  // even before an effect was picked.
  const backgroundButtonLabel = hasBackgroundEffect
    ? describeBackgroundEffect(backgroundEffect)
    : 'Background & effects'

  // Call Stability Fix: Emit dialog opened BEFORE any system permission prompt.
  // useLayoutEffect runs before useEffect, so this fires before useMediaDevices requests getUserMedia.
  useLayoutEffect(() => {
    if (open) {
      emitDialogOpened('permissions')
    } else {
      emitDialogClosed('permissions')
    }
  }, [open])

  // Check if we're operating on a password-protected room
  useEffect(() => {
    if (open) {
      
      const roomId = localStorage.getItem('pendingRoomId')
      const roomPassword = localStorage.getItem('pendingRoomPassword')

      // If we're trying to join a private room but don't have the password
      if (roomId) {
        // console.log(`PermissionsDialog opening for room ${roomId}`)

        if (localStorage.getItem('roomHasPassword') === 'true' && !roomPassword) {
          // console.warn('Attempting to join private room without password')
          // Auto-close dialog - user needs to enter password first
          onClose()
        }
      }
    }
  }, [open, onClose])


  const [backgroundWaitExpired, setBackgroundWaitExpired] = useState(false)
  // Re-armed by each new pick (the selection changes), so a later pick is held
  // for too, even after an earlier one hit the cap.
  useEffect(() => {
    setBackgroundWaitExpired(false)
    if (!backgroundEffectApplying) return
    const timer = setTimeout(() => setBackgroundWaitExpired(true), JOIN_BACKGROUND_WAIT_CAP_MS)
    return () => clearTimeout(timer)
  }, [backgroundEffectApplying, backgroundEffect])

  const handleJoin = useCallback(async () => {
    let networkInstance = network

    if (!networkInstance) {
      console.error('No network instance provided to PermissionsDialog')
      // Try to get from Bootstrap as a fallback
      try {
        if (phaserGame.scene.keys.bootstrap) {
          console.log('Attempting to get network from Bootstrap scene as fallback')
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const bootstrap = phaserGame.scene.keys.bootstrap as any
          if (bootstrap.network) {
            console.log('Successfully retrieved network from Bootstrap')
            networkInstance = bootstrap.network
          } else {
            console.error('Bootstrap exists but has no network property')
            return
          }
        } else {
          console.error('Bootstrap scene not available')
          return
        }
      } catch (error) {
        console.error('Error getting network from Bootstrap:', error)
        return
      }
    }

    try {
      // Set loading game state to true and KEEP it true throughout the transition
      dispatch(setLoadingGame(true))
      console.log('PermissionsDialog: Permissions granted, continuing with join process')

      // Ask for notification permission alongside camera/mic, while we still have
      // the Join click's user gesture. Fire-and-forget — a denied/blocked prompt
      // must never block joining the game.
      void requestNotificationPermission()

      // Validate the device IDs are actually available
      let currentVideoDeviceId = ''
      let currentAudioDeviceId = ''

      // Get all available devices to validate against
      try {
        const devices = await navigator.mediaDevices.enumerateDevices()
        // The fresh list decides, not the one the dialog holds: that one is as
        // old as the last time the hook listed, and a device turned on since was
        // blank in it until it was granted.
        //
        // A kind listed without ids (not granted yet) saves nothing: the picker
        // would keep the selected id for the select's sake, but writing it here
        // would claim a device the browser has not named, so the saved one stays.
        const listedVideo = devices.filter((device) => device.kind === 'videoinput')
        const listedAudio = devices.filter((device) => device.kind === 'audioinput')
        currentVideoDeviceId = listedWithoutIds(listedVideo)
          ? ''
          : pickListedDevice(state.selectedVideoDevice, listedVideo)
        currentAudioDeviceId = listedWithoutIds(listedAudio)
          ? ''
          : pickListedDevice(state.selectedAudioDevice, listedAudio)
      } catch (err) {
        // Fallback to using the selected values directly
        currentVideoDeviceId = state.selectedVideoDevice
        currentAudioDeviceId = state.selectedAudioDevice
      }

      // Save current device preferences with validated IDs. An empty one is a
      // device the browser lists without an id before it is granted: written
      // over the saved id, the call would open the default device instead.
      if (currentVideoDeviceId) localStorage.setItem('preferredVideoDevice', currentVideoDeviceId)
      if (currentAudioDeviceId) localStorage.setItem('preferredAudioDevice', currentAudioDeviceId)
      // What the dialog shows, because LiveKit starts the session from these
      // alone. An off that an error forced is marked so the next Join screen
      // tries the device again.
      writeMediaPreference('video', state.videoEnabled, { forced: state.videoForcedOff })
      writeMediaPreference('audio', state.audioEnabled, { forced: state.audioForcedOff })

      // Set media settings in Network if the class has the relevant methods
      if (networkInstance?.webRTC && state.stream) {
        // WebRTC setup would go here
      }

      // Get stored room ID from localStorage if available
      const pendingRoomId = localStorage.getItem('pendingRoomId')

      // Bug C fix: Synchronously release the camera/mic preview stream BEFORE
      // handing off to LiveKit. This prevents a race condition where LiveKit's
      // getUserMedia/setCameraEnabled fails because the preview still holds an
      // exclusive lock on the camera device.
      stopPreviewTracks()

      trackEvent(
        'APP_permissions_allowed',
        stripUndefinedAnalyticsProps({
          camera_enabled: !!state.videoEnabled,
          mic_enabled: !!state.audioEnabled,
          permission_origin: pendingRoomId ? 'pre_join' : 'in_app',
        })
      )
      await onPermissionsGranted(
        inviteJoinNear?.visible ? { joinNearHost: inviteJoinNear.checked } : undefined
      )
      onClose()
    } catch (error) {
      console.error('Error during permission setup:', error)
      setState({
        type: 'SET_ERROR',
        error: 'Failed to set up permissions. Please try again or check browser settings.',
      })
      // Only turn off loading state if there was an error
      dispatch(setLoadingGame(false))
    }
  }, [
    network,
    state.selectedVideoDevice,
    state.selectedAudioDevice,
    state.videoEnabled,
    state.audioEnabled,
    state.videoForcedOff,
    state.audioForcedOff,
    state.stream,
    dispatch,
    onPermissionsGranted,
    onClose,
    setState,
    stopPreviewTracks,
    inviteJoinNear,
  ])

  const handleDeviceSelectionChange = useCallback(
    (type: 'video' | 'audio', event: SelectChangeEvent<string>) => {
      handleDeviceSelection(type, event.target.value)
    },
    [handleDeviceSelection]
  )

  const renderVideoPreview = useCallback(() => {
    if (state.initializing) {
      return (
        <LoadingWrapper>
          <CircularProgress />
          <Typography>Initializing camera and microphone...</Typography>
        </LoadingWrapper>
      )
    }

    if (state.loading) {
      return (
        <LoadingWrapper>
          <CircularProgress />
          <Typography>Switching devices...</Typography>
        </LoadingWrapper>
      )
    }

    // Always render media controls, regardless of video state
    const mediaControls = (
      <MediaControlsWrapper>
        <Tooltip title={state.videoEnabled ? 'Disable camera' : 'Enable camera'}>
          <MediaControlButton
            onClick={handleVideoToggle}
            className={!state.videoEnabled ? 'disabled' : ''}
            size="large"
          >
            {state.videoEnabled ? <VideocamIcon /> : <VideocamOffIcon />}
          </MediaControlButton>
        </Tooltip>
        <Tooltip title={state.audioEnabled ? 'Disable microphone' : 'Enable microphone'}>
          <MediaControlButton
            onClick={handleAudioToggle}
            className={!state.audioEnabled ? 'disabled' : ''}
            size="large"
          >
            {state.audioEnabled ? <MicIcon /> : <MicOffIcon />}
          </MediaControlButton>
        </Tooltip>
        <Tooltip title="Refresh devices">
          {(state.loading || state.initializing) ? (
            <span>
              <MediaControlButton
                onClick={handleRefresh}
                size="large"
                disabled={state.loading || state.initializing}
              >
                <RefreshIcon />
              </MediaControlButton>
            </span>
          ) : (
            <MediaControlButton
              onClick={handleRefresh}
              size="large"
              disabled={state.loading || state.initializing}
            >
              <RefreshIcon />
            </MediaControlButton>
          )}
        </Tooltip>
      </MediaControlsWrapper>
    )

    // Determine whether to show the "no video" overlay.
    // With track.enabled toggling the video tracks stay on the stream, so we
    // check both the user preference AND whether tracks actually exist.
    const hasVideoTrack = (state.stream?.getVideoTracks().length ?? 0) > 0
    const shouldShowNoVideo = !state.videoEnabled || !hasVideoTrack

    // Check if video track is actually active (track exists AND enabled AND live)
    const videoTrack = state.stream?.getVideoTracks()[0]
    const isVideoActive = videoTrack?.readyState === 'live' && !videoTrack?.muted && videoTrack?.enabled !== false

    /**
     * Keep the panel covered until the element has painted, not merely until the
     * processed stream exists.
     *
     * Those are different moments, and the gap between them is a **black frame**:
     * binding a new `srcObject` blanks the element, so lifting the cover when the
     * stream arrives shows the chosen background, then black, then the camera.
     * Waiting for a painted frame collapses that into one transition.
     *
     * This also covers a plain camera start with no effect selected, where the
     * same blank frame exists and was simply never hidden.
     *
     * Except where the raw view a pick was made over is still painted beneath
     * the element (see useMediaDevices' preview rule): that view is what the
     * user was looking at, and it stays until the effect has painted above it.
     */
    const previewNotReady = previewEffectPending || !previewPainted
    const previewCovered =
      !shouldShowNoVideo && (previewEffectPending || !previewPainted) && !previewUnderlayShowing

    return (
      <>
        {/* Always keep the video element mounted so the ref is available for
            srcObject assignment. Hide it via CSS when video should not be shown.
            This eliminates the mount-gap bug where toggling video off→on caused
            the StyledVideo to remount but the useEffect([stream]) had already
            fired with videoRef.current === null. */}
        <StyledVideo
          ref={videoRef}
          data-testid="prejoin-preview"
          autoPlay
          playsInline
          muted
          style={{
            display: shouldShowNoVideo ? 'none' : 'block',
            transition: PREVIEW_EFFECT_TRANSITION,
            ...(previewNotReady ? previewPreparingStyle : previewSettledStyle),
          }}
          onLoadedMetadata={() => {
            console.log('[PermissionsDialog] Video metadata loaded')
          }}
          onError={(e) => {
            console.error('[PermissionsDialog] Video element error:', e)
          }}
        />
        {/* Beneath the preview: the raw view a pick was made over, for the
            moment the processed stream takes the element over. Bound only while
            that pick is held or handing over; shown only while the preview
            above it has not painted. */}
        <video
          ref={setUnderlayElement}
          autoPlay
          playsInline
          muted
          aria-hidden
          data-testid="preview-handover-underlay"
          style={{
            ...previewUnderlayStyle,
            display: !shouldShowNoVideo && previewUnderlayStream ? 'block' : 'none',
          }}
        />
        {previewCovered && (
          /* Opaque on purpose. Underneath is the processor's pass-through
             while an effect starts that was not picked over the raw view — the
             room the user asked to hide — or nothing at all yet. A held
             preview says why here, and the notice beside it keeps only what
             can be done about it. */
          <Box data-testid="preview-effect-cover" sx={previewCoverSx}>
            {previewProblem && backgroundProblemHoldsCamera(previewProblem) && (
              <Typography
                variant="body2"
                id={previewProblemMessageId}
                role="alert"
                data-testid="preview-effect-cover-reason"
              >
                {backgroundProblemTooltip(previewProblem, 'join')}
              </Typography>
            )}
          </Box>
        )}
        {previewEffectWorking && !shouldShowNoVideo && (
          /* Only while there is real work to wait for. A plain camera start is
             covered too, but briefly, and a progress bar for it would read as a
             problem rather than a wait. */
          <LinearProgress
            data-testid="preview-effect-progress"
            aria-label="Applying background"
            sx={previewProgressSx}
          />
        )}
        {shouldShowNoVideo && (
          <NoVideoFeedback>
            <VideocamOffIcon sx={{ fontSize: 48 }} />
            <Typography>
              {!state.videoEnabled 
                ? 'Camera is disabled' 
                : state.initializing 
                  ? 'Initializing camera...' 
                  : 'No camera feed available'}
            </Typography>
            {state.cameraError && (
              <Typography color="error" variant="caption" sx={{ mt: 1, textAlign: 'center', maxWidth: '80%' }}>
                {state.cameraError}
              </Typography>
            )}
            {!state.cameraError && !state.videoEnabled && state.audioEnabled && (
              <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary' }}>
                You can still join with audio only
              </Typography>
            )}
          </NoVideoFeedback>
        )}
        {mediaControls}
        {!shouldShowNoVideo && !isVideoActive && videoTrack && (
          <Alert 
            severity="warning" 
            sx={{ 
              position: 'absolute', 
              bottom: 16, 
              left: 16, 
              right: 16,
              ...alertWarningSx,
            }}
          >
            Camera feed may not be working properly. Try refreshing devices or enabling the camera.
          </Alert>
        )}
      </>
    )
  }, [
    previewEffectPending,
    previewEffectWorking,
    previewPainted,
    previewProblem,
    previewProblemMessageId,
    previewUnderlayShowing,
    previewUnderlayStream,
    state.initializing,
    state.loading,
    state.videoEnabled,
    state.audioEnabled,
    state.stream,
    state.cameraError,
    videoRef,
    handleVideoToggle,
    handleAudioToggle,
    handleRefresh,
    alertWarningSx,
  ])

  const renderPermissionStatus = useCallback(() => {
    if (!state.permissionsRequested) return null

    // Don't show the status next to a device error: the error is more helpful,
    // and "permissions granted" beside it reads as if everything worked.
    if (state.cameraError || state.microphoneError) {
      return null
    }

    return (
      <PermissionStatusContainer>
        <StatusIcon status={state.permissionsGranted ? 'success' : 'error'}>
          {state.permissionsGranted ? <CheckCircleIcon /> : <ErrorOutlineIcon />}
        </StatusIcon>
        <Typography>
          {state.permissionsGranted
            ? 'Camera and microphone permissions granted'
            : 'Camera and/or microphone permissions denied. Please allow access in your browser settings and refresh.'}
        </Typography>
      </PermissionStatusContainer>
    )
  }, [state.permissionsRequested, state.permissionsGranted, state.cameraError, state.microphoneError])

  const isJoinButtonDisabled = useCallback(() => {
    // Entering the office is NOT conditional on working media.
    //
    // This previously required `permissionsGranted`, so any getUserMedia failure
    // — no camera at all, a broken or unplugged one, one held by another app, or
    // a denied prompt — left the user permanently stuck at the door with no way
    // in. It also refused a join with both devices off. Neither is defensible:
    // people without cameras still need to get into the office, and someone who
    // just wants to look around should not be forced to broadcast.
    //
    // The only remaining reasons to wait are genuinely transient.
    return state.loading || state.initializing
  }, [state.loading, state.initializing])

  /**
   * Say what the user is actually about to join with, so "no devices" reads as a
   * deliberate choice rather than something silently broken.
   */
  const joinButtonLabel =
    state.videoEnabled || state.audioEnabled
      ? 'Join with selected devices'
      : 'Join without camera or microphone'

  const inviteBlocked = !!(inviteGate?.blocked || inviteGate?.loading)
  // Join hands the call the pick the connector holds, so a pick that is not yet
  // the saved choice has to land (or fail) first. Normally that ends by itself;
  // the cap is the backstop so a pick that never settles cannot lock Join.
  const waitingForDevices = state.loading || state.initializing
  const waitingForBackground =
    !waitingForDevices && backgroundEffectApplying && !backgroundWaitExpired
  const joinDisabled = isJoinButtonDisabled() || inviteBlocked || waitingForBackground

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          zIndex: zIndexLayers.dialogs,
        },
      }}
    >
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6">Camera & Microphone Settings</Typography>
        </Box>
      </StyledDialogTitle>

      <StyledDialogContent>
        {inviteGate?.loading && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Verifying room link…
          </Alert>
        )}

        {inviteGate?.blocked && inviteGate.blockMessage && (
          <Alert severity="error" sx={{ mb: 2, ...alertErrorSx }} role="alert">
            {inviteGate.blockMessage}
          </Alert>
        )}

        {inviteJoinNear?.visible && !inviteGate?.blocked && !inviteGate?.loading && (
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={inviteJoinNear.checked}
                  onChange={(_, c) => inviteJoinNear.onChange(c)}
                  color="primary"
                />
              }
              label="Join near the person who shared this link (same area of the office)"
            />
          </Box>
        )}

        {state.error && !state.cameraError && !state.microphoneError && (
          <Alert
            severity="error"
            sx={{ 
              mb: 2,
              ...alertErrorSx,
            }}
            onClose={() => setState({ type: 'SET_ERROR', error: null })}
          >
            {state.error}
          </Alert>
        )}

        {renderPermissionStatus()}

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <VideoPreviewContainer>{renderVideoPreview()}</VideoPreviewContainer>
          </Grid>

          <Grid item xs={12} md={4}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <DeviceSelectCard>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CameraAltIcon sx={{ color: 'primary.main' }} />
                    <Typography variant="subtitle1">Camera</Typography>
                  </Box>
                  <FormControl fullWidth>
                    <InputLabel sx={deviceInputLabelSx}>
                      Select Camera
                    </InputLabel>
                    <Select
                      value={state.selectedVideoDevice}
                      onChange={(e) => handleDeviceSelectionChange('video', e)}
                      label="Select Camera"
                      disabled={state.loading || !state.permissionsGranted}
                      sx={deviceSelectSx}
                    >
                      {state.videoDevices.map((device) => (
                        <MenuItem key={device.deviceId} value={device.deviceId}>
                          {describeMediaDevice(device, 'videoinput')}
                        </MenuItem>
                      ))}
                      {state.videoDevices.length === 0 && (
                        <MenuItem value="" disabled>
                          No cameras found
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                  {backgroundEffectsAvailable && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5 }}>
                      <Tooltip title={backgroundButtonLabel}>
                        {/* A disabled button fires no events, so the tooltip
                            needs an element that does. */}
                        <Box component="span" sx={{ display: 'flex', flex: 1, minWidth: 0 }}>
                          <Button
                            fullWidth
                            variant="outlined"
                            size="small"
                            startIcon={<WallpaperIcon />}
                            onClick={() => setBackgroundDialogOpen(true)}
                            disabled={state.loading}
                            data-testid="open-background-effects"
                            sx={{
                              textTransform: 'none',
                              justifyContent: 'flex-start',
                              // This sits in the narrow settings column beside a
                              // clear button, so the label has little room. One
                              // line that truncates reads better than two ragged
                              // ones — the tooltip carries the full text either
                              // way.
                              minWidth: 0,
                              '& .MuiButton-startIcon': { flexShrink: 0 },
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {backgroundButtonLabel}
                            </Box>
                          </Button>
                        </Box>
                      </Tooltip>
                      {hasBackgroundEffect && (
                        <Tooltip title="Remove background effect">
                          <IconButton
                            size="small"
                            onClick={() => handleBackgroundEffectChange(NO_BACKGROUND_EFFECT)}
                            aria-label="Remove background effect"
                            data-testid="clear-background-effect"
                            // Never squeezed by a long label.
                            sx={{ flexShrink: 0 }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                  {previewProblem && !backgroundDialogOpen && (
                    <BackgroundProblemNotice
                      problem={previewProblem}
                      onAction={handleBackgroundProblemAction}
                      // The preview already says it; repeating it here was
                      // the same words twice, side by side.
                      messageShownAt={previewShowsProblem ? previewProblemMessageId : undefined}
                    />
                  )}
                  {state.cameraError && (
                    <Alert 
                      severity="error" 
                      sx={{ 
                        mt: 1,
                        ...alertErrorSx,
                      }}
                    >
                      {state.cameraError}
                    </Alert>
                  )}
                </DeviceSelectCard>
              </Grid>

              <Grid item xs={12}>
                <DeviceSelectCard>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <MicIcon sx={{ color: 'primary.main' }} />
                    <Typography variant="subtitle1">Microphone</Typography>
                  </Box>
                  
                  <FormControl fullWidth>
                    <InputLabel sx={deviceInputLabelSx}>
                      Select Microphone
                    </InputLabel>
                    <Select
                      value={state.selectedAudioDevice}
                      onChange={(e) => handleDeviceSelectionChange('audio', e)}
                      label="Select Microphone"
                      disabled={state.loading || !state.permissionsGranted}
                      sx={deviceSelectSx}
                    >
                      {state.audioDevices.map((device) => (
                        <MenuItem key={device.deviceId} value={device.deviceId}>
                          {describeMediaDevice(device, 'audioinput')}
                        </MenuItem>
                      ))}
                      {state.audioDevices.length === 0 && (
                        <MenuItem value="" disabled>
                          No microphones found
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" id="mic-level-label">
                      {state.audioEnabled ? 'Say something — the bar moves when the microphone hears you' : 'Microphone is off'}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={Math.round(micLevel * 100)}
                      aria-labelledby="mic-level-label"
                      data-testid="mic-level-bar"
                      sx={{ mt: 0.75, height: 6, borderRadius: 3 }}
                    />
                  </Box>

                  <Button
                    onClick={handleSpeakerTest}
                    disabled={speakerTestPlaying}
                    startIcon={<VolumeUpIcon />}
                    size="small"
                    sx={{ mt: 1.5, textTransform: 'none' }}
                  >
                    {speakerTestPlaying ? 'Playing…' : 'Test speaker'}
                  </Button>

                  {state.microphoneError && (
                    <Alert 
                      severity="error" 
                      sx={{ 
                        mt: 1,
                        ...alertErrorSx,
                      }}
                    >
                      {state.microphoneError}
                    </Alert>
                  )}
                </DeviceSelectCard>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </StyledDialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          backgroundColor: 'action.hover',
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Button onClick={onClose} sx={cancelButtonSx}>
          Cancel
        </Button>
        <Button
          onClick={handleJoin}
          variant="contained"
          disabled={joinDisabled}
          startIcon={
            waitingForDevices || waitingForBackground ? (
              <CircularProgress size={18} sx={{ color: 'primary.contrastText' }} />
            ) : null
          }
          sx={joinButtonSx}
        >
          {waitingForDevices
            ? 'Please wait...'
            : waitingForBackground
              ? 'Applying background...'
              : joinButtonLabel}
        </Button>
      </DialogActions>

      {backgroundEffectsAvailable && (
        <BackgroundEffectsDialog
          open={backgroundDialogOpen}
          onClose={() => setBackgroundDialogOpen(false)}
          previewStream={previewStream}
          handoverFrom={previewHandoverFrom}
          cameraEnabled={state.videoEnabled}
          effectPending={previewEffectPending}
          effectWorking={previewEffectWorking}
          problem={previewProblem}
          onProblemAction={handleBackgroundProblemAction}
          value={backgroundEffect}
          onChange={handleBackgroundEffectChange}
          showing={previewShowing}
          disabled={state.loading}
        />
      )}
    </StyledDialog>
  )
}
